import {
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  FilterAlt as FilterAltIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReusableTable from '../../common/table/CommonTable';
import { fetchAllReportList } from '../../redux/admissions/admissionsActions';

// Suppress ResizeObserver loop error
if (typeof window !== 'undefined') {
  const resizeObserverErrorHandler = (e) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
      e.stopImmediatePropagation();
    }
  };
  window.addEventListener('error', resizeObserverErrorHandler);
}

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BLUE_THEME = {
  primary: '#1976d2',
  secondary: '#42a5f5',
  light: '#e3f2fd',
  dark: '#0d47a1',
  gradient: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
};

const columns = [
  { id: 'serialNo', label: 'S.No', numeric: true },
  { id: 'patientName', label: 'Patient Name' },
  { 
    id: 'admissionDate', 
    label: 'Admission Date',
    render: (value) => value ? formatDate(new Date(value)) : 'N/A'
  },
  { 
    id: 'dischargeDate', 
    label: 'Discharge Date',
    render: (value) => value ? formatDate(new Date(value)) : 'Pending'
  },
  { id: 'attenderName1', label: 'Attender Name 1' },
  { id: 'attenderName2', label: 'Attender Name 2' },
  { id: 'patientPhone', label: 'Patient Phone' },
  { id: 'attenderPhone1', label: 'Attender Phone 1' },
  { id: 'attenderPhone2', label: 'Attender Phone 2' },
  { id: 'address', label: 'Address' },
  { id: 'wardName', label: 'Ward' },
  { id: 'blockName', label: 'Block' },
  { 
    id: 'patientStatus', 
    label: 'Patient Status',
    render: (value) => (
      <Chip 
        label={value} 
        color={value === 'Admitted' ? 'primary' : 'default'} 
        size="small" 
        sx={{
          fontWeight: 'bold',
          bgcolor: value === 'Admitted' ? BLUE_THEME.primary : BLUE_THEME.secondary,
          color: 'white'
        }}
      />
    )
  },
  { 
    id: 'attenderStatus', 
    label: 'Attender Status',
    render: (value) => (
      <Chip 
        label={value} 
        size="small" 
        sx={{
          fontWeight: 'bold',
          bgcolor: value === 'Active' ? BLUE_THEME.primary : '#757575',
          color: 'white'
        }}
      />
    )
  },
];

const DailyLogReport = () => {
  const dispatch = useDispatch();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of day
    return today;
  });
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const reportData = useSelector((state) => state?.admission?.fetchReport || {});
  const admissions = reportData?.admissions?.data || [];

  const totalPages = reportData?.admissions?.totalPages || 0;

  const fetchData = (payload) => {
    dispatch(fetchAllReportList(payload));
  };

  const buildPayload = () => {
    return {
      pageNo: currentPage,
      pagesize: 10,
      pagination_required: true,
      search_by: searchText,
      start_date: formatDateForAPI(startDate),
      end_date: formatDateForAPI(endDate),
    };
  };

  useEffect(() => {
    // Only fetch data if both start and end dates are selected
    if (startDate && endDate) {
      const payload = buildPayload();
      fetchData(payload);
    }
  }, [currentPage, searchText, startDate, endDate]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    if (reportData.success && admissions.length > 0) {
      const processedData = admissions.map((admission, index) => {
        const patient = admission.Patient || {};
        const ward = admission.Ward || {};
        const block = admission.Block || {};
        const attenders = admission.Attenders || [];
        const attender1 = attenders[0] || {};
        const attender2 = attenders[1] || {};
        
        return {
          serialNo: admission.admission_no,
          id: admission.id,
          patientName: patient.patient_name || 'N/A',
          admissionDate: admission.admit_date,
          dischargeDate: admission.discharge_date,
          attenderName1: attender1.attender_name || 'N/A',
          attenderName2: attender2.attender_name || '',
          patientPhone: patient.phone || 'N/A',
          attenderPhone1: attender1.phone || 'N/A',
          attenderPhone2: attender2.phone || '',
          address: (attender1.address || patient.address || 'N/A'),
          wardName: ward.ward_name || 'N/A',
          blockName: block.block_name || 'N/A',
          patientStatus: admission.discharge_date ? 'Discharged' : 'Admitted',
          attenderStatus: (attenders.length > 0 && attenders[0].attender_status === 1) ? 'Active' : 'Inactive'
        };
      });
      
      setPatients(processedData);
    } else if (reportData.hasOwnProperty('success') && !reportData.success) {
      setPatients([]);
    }
  }, [reportData]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleDateChange = (event, isStart) => {
    const newDate = new Date(event.target.value);
    if (isStart) {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  };

  const handleRefresh = () => {
    if (startDate && endDate) {
      const payload = buildPayload();
      setLoading(true);
      dispatch(fetchAllReportList(payload)).catch((error) => {
        console.error('Error refreshing data:', error);
        setLoading(false);
      });
    }
  };

  const dateToInputValue = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  return (
    <Box sx={{ p: 3, background: '#f8faff', minHeight: '100vh' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          borderRadius: '16px',
          background: 'white',
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.08)',
          border: `1px solid ${BLUE_THEME.light}`,
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: BLUE_THEME.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <CalendarTodayIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="bold"
                sx={{ color: BLUE_THEME.primary, mb: 0.5 }}
              >
                Patient Reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and view patient admission records
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              sx={{ 
                color: BLUE_THEME.primary,
                bgcolor: BLUE_THEME.light,
                '&:hover': { 
                  bgcolor: BLUE_THEME.secondary + '20',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filters Card */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 4, 
            bgcolor: BLUE_THEME.light,
            border: `1px solid ${BLUE_THEME.secondary}30`,
            borderRadius: '12px',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <FilterAltIcon sx={{ color: BLUE_THEME.primary, mr: 1 }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ color: BLUE_THEME.primary }}
              >
                Date Range & Search
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {/* Start Date */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={dateToInputValue(startDate)}
                  onChange={(e) => handleDateChange(e, true)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: BLUE_THEME.secondary + '50',
                      },
                      '&:hover fieldset': {
                        borderColor: BLUE_THEME.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: BLUE_THEME.primary,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: BLUE_THEME.primary,
                    },
                  }}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={dateToInputValue(endDate)}
                  onChange={(e) => handleDateChange(e, false)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: BLUE_THEME.secondary + '50',
                      },
                      '&:hover fieldset': {
                        borderColor: BLUE_THEME.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: BLUE_THEME.primary,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: BLUE_THEME.primary,
                    },
                  }}
                />
              </Grid>

              {/* Search */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by name, phone, ward, block..."
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: BLUE_THEME.secondary + '50',
                      },
                      '&:hover fieldset': {
                        borderColor: BLUE_THEME.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: BLUE_THEME.primary,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: BLUE_THEME.primary,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: BLUE_THEME.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Date Range Summary */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: BLUE_THEME.light,
              border: `1px solid ${BLUE_THEME.secondary}30`,
              borderRadius: '25px',
            }}
          >
            <Typography 
              variant="body1" 
              fontWeight="bold"
              sx={{ color: BLUE_THEME.primary }}
            >
              📅 {formatDate(startDate)} - {formatDate(endDate)}
            </Typography>
          </Paper>
        </Box>

        {/* Table */}
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${BLUE_THEME.light}`,
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              bgcolor: BLUE_THEME.light,
              borderBottom: `1px solid ${BLUE_THEME.secondary}30`,
            }}
          >
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ color: BLUE_THEME.primary }}
            >
              Patient Records ({patients.length})
            </Typography>
          </Box>

          <ReusableTable
            columns={columns}
            data={patients}
            isLoading={reportData.inProgress}
            title="Patient Report"
            headerColor={BLUE_THEME.primary}
            rowColor="#ffffff"
            alternateRowColor="#f8faff"
            withPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showDownloadOption={false}
            withActions={false}
            customEmptyMessage="No patient records found for the selected date range"
          />
        </Paper>
      </Paper>
    </Box>
  );
};

export default DailyLogReport;